import axios from '../lib/axios';
import { AxiosError } from 'axios';
import { UserRole } from '../types/auth';

/**
 * Login API Response interface - Adapted to actual API response format
 */
interface LoginResponse {
  status: string;
  result?: {
    otpHash?: string;  // Correct property name is otpHash, not hash
  };
}

/**
 * Error Response interface
 */
interface ErrorResponse {
  message: string;
  statusCode: number;
}

/**
 * API Response for OTP verification
 */
interface VerifyOtpResponse {
  status: string;
  result?: {
    token?: string;
    role?: string;
    name?: string;
    email?: string;
    suspended?: boolean;
    activated?: boolean;
    lastLogin?: string;
    dashPermissions?: string[];
    groups?: string[]; // Add groups array for cleverpeuser role
    lender? : string[];
  };
}

/**
 * Login function - Calls the login API to send OTP
 * Uses hardcoded "admin" type
 * 
 * @param mobileNo Mobile number
 * @returns Promise with login response containing OTP hash
 */
export const login = async (mobileNo: string): Promise<{ otpHash: string }> => {
  try {
    console.log('[api] Sending login request for:', mobileNo);
    
    const response = await axios.post<LoginResponse>('/auth/v1/login', {
      mobileNo,
      type: "admin" // Hardcoded as per requirements
    });
    
    console.log('[api] Full login response:', response.data);
    
    // Since the API is returning a successful status but we need to extract the hash correctly
    if (response.data.status === "Request successful!") {
      // Check if we can find hash in response data
      let hash: string | undefined;
      
      // First try result.otpHash (correct property name)
      if (response.data.result && response.data.result.otpHash) {
        hash = response.data.result.otpHash;
      }
      
      // If hash is found, return it
      if (hash) {
        console.log('[api] Found hash in response:', hash.substring(0, 15) + '...');
        return { otpHash: hash };
      }
      
      // If no hash is found, this is an error
      console.error('[api] No hash found in successful response:', response.data);
      throw new Error('Invalid response from server: No hash found');
    }
    
    throw new Error('Invalid response from server');
  } catch (error) {
    // Check if it's an axios error with response data
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response && axiosError.response.data) {
      console.error('[api] API Error:', axiosError.response.data);
      throw new Error(axiosError.response.data.message || 'Login failed. Please try again.');
    }
    
    // Network or other errors
    console.error('[api] Network Error:', error);
    throw new Error('Unable to connect to the server. Please check your internet connection.');
  }
};

/**
 * Helper function to convert API boolean string values to actual Booleans
 * @param value Value to convert
 * @returns Proper boolean value
 */

/**
 * Verify OTP function
 * 
 * @param mobileNo Mobile number
 * @param otp OTP entered by user
 * @param otpHash OTP hash received from login
 * @returns Promise with verification response containing token and user info
 */
export const verifyOtp = async (mobileNo: string, otp: string, otpHash: string) => {
  console.log('[api] Making verify OTP request with:', { 
    mobileNo,
    otp,
    otpHash: otpHash ? `${otpHash.substring(0, 15)}...` : 'missing'
  });
  
  try {
    // Check if we have all required params
    if (!mobileNo || !otp || !otpHash) {
      console.error('[api] Missing required params for OTP verification');
      throw new Error('Missing required parameters for OTP verification');
    }
    
    // Make sure OTP is a string (backend will parse it as number)
    const otpString = String(otp).trim();
    
    // Ensure OTP is a valid number (backend uses parseInt)
    if (!/^\d+$/.test(otpString)) {
      throw new Error('OTP must contain only numbers');
    }
    
    // Make the actual API request with the correct parameter format
    const requestBody = {
      mobileNo: mobileNo,
      otp: otpString,
      otpHash: otpHash
    };
    
    console.log('[api] Sending OTP verification request with body:', {
      ...requestBody,
      otpHash: requestBody.otpHash.substring(0, 15) + '...' // Truncate hash for logging
    });
    
    const response = await axios.post<VerifyOtpResponse>('/auth/v1/verify', requestBody);
    
    console.log('[api] Verify OTP response:', response.data);
    
    // Check for successful status
    if (response.data.status !== "Request successful!") {
      throw new Error('OTP verification failed. Please try again.');
    }
    
    // Check if we have the required data in the response
    if (!response.data.result || !response.data.result.token || !response.data.result.role) {
      throw new Error('Invalid response from server: Missing required data');
    }
    
    // Log the raw result
    console.log('[api] Raw API response result:', response.data.result);
    
    // Extract all available user data from the response
    const { token, role, name, email, suspended, activated, lastLogin, dashPermissions, groups,lender } = response.data.result;
    
    // Construct user data with the information we have
    const userData = {
      token,
      user: {
        name: name || '',
        email: email || '',
        role: role as UserRole,
        mobileNo: mobileNo, // Use request mobileNo since API response doesn't include it
        suspended: suspended === true, // Ensure it's a boolean
        activated: activated === true, // Ensure it's a boolean
        lastLogin: lastLogin || '',
        dashPermissions: dashPermissions || [], // Add dashPermissions to user data
        groups: groups || [], // Add groups to user data
        lender: lender || [] // Add lender to user data
      }
    };
    
    console.log('[DEBUG] Final constructed user data:', userData);
    return userData;
  } catch (error) {
    console.error('[api] Verify OTP error:', error);
    
    // Check if it's an axios error with response data
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response && axiosError.response.data) {
      console.error('[api] API Error response:', axiosError.response.data);
      throw new Error(axiosError.response.data.message || 'OTP verification failed. Please try again.');
    }
    
    // Re-throw original error if it's not an axios error or doesn't have expected structure
    if (error instanceof Error) {
      throw error;
    }
    
    // Network or other errors
    throw new Error('Unable to connect to the server. Please check your internet connection.');
  }
};

/**
 * Validate 10-digit Indian mobile number
 * 
 * @param mobileNo Mobile number to validate
 * @returns Boolean indicating if the mobile number is valid
 */
export const isValidMobileNumber = (mobileNo: string): boolean => {
  const pattern = /^[6-9]\d{9}$/;
  return pattern.test(mobileNo);
}; 